/**
 * Structured result of a PIX payment countdown computation.
 *
 * Two call sites in PlanCheckout previously diverged:
 *   1. `formatExpirationTime` (line ~210): decomposed diffMs into d/h/m/s for display.
 *   2. Inline IIFE (line ~760): computed `minutesLeft = Math.floor(diffMs / 60000)` for badge CSS class.
 *
 * This unified function returns all fields so both call sites can be replaced without
 * behavioral regression. The component still handles i18n string assembly and CSS class selection.
 *
 * QUIRK: the original implementations each created `new Date()` independently, so a
 * sub-second gap between the two reads was possible. This function accepts `now` to
 * eliminate the gap — callers pass `currentTime` state which is updated every second.
 */
export interface PixCountdown {
  /** true when the expiry timestamp is in the past (diffMs <= 0) */
  expired: boolean;
  /** Whole days remaining */
  days: number;
  /** Hours remaining after subtracting whole days */
  hours: number;
  /** Minutes remaining after subtracting whole hours */
  minutes: number;
  /** Seconds remaining after subtracting whole minutes */
  seconds: number;
  /**
   * Floor of total minutes remaining (Math.floor(diffMs / 60000)).
   * Used by the urgency-badge CSS class selector (<= 2 → red, <= 5 → orange, else amber).
   */
  totalMinutes: number;
}

/**
 * Computes the PIX payment countdown from a given expiry timestamp.
 *
 * @param expiresAt - ISO 8601 expiry timestamp string (e.g. `pixPaymentData.expires_at`).
 * @param now       - Current time, injected to keep this function pure.
 * @returns         A PixCountdown object with all fields set to zero when expired.
 */
export function formatPixCountdown(expiresAt: string, now: Date): PixCountdown {
  const expirationDate = new Date(expiresAt);
  const diffMs = expirationDate.getTime() - now.getTime();

  if (diffMs <= 0) {
    return { expired: true, days: 0, hours: 0, minutes: 0, seconds: 0, totalMinutes: 0 };
  }

  const totalSeconds = Math.floor(diffMs / 1000);
  const days = Math.floor(totalSeconds / (24 * 60 * 60));
  const hours = Math.floor((totalSeconds % (24 * 60 * 60)) / (60 * 60));
  const minutes = Math.floor((totalSeconds % (60 * 60)) / 60);
  const seconds = totalSeconds % 60;
  const totalMinutes = Math.floor(diffMs / 60000);

  return { expired: false, days, hours, minutes, seconds, totalMinutes };
}
